/**
 * OsCountSessionList — MADE-293
 *
 * Renders session cards with live progress rings, status chip, pending/approved/rejected
 * breakdown, and a Review button. Polls GET /api/os/audit/sessions every 30s.
 *
 * Props:
 *   onReview          — called when the manager taps "Review →"
 *   onSessionsLoaded  — (optional) called after each successful fetch so the parent
 *                       can maintain a copy of allSessions for compare mode
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CountSession {
  sessionId: string;
  name: string;
  location: string;
  startedAt: string;
  closedAt: string | null;
  // F3: full status union — 'expired' was previously collapsed to 'closed' in the UI chip
  status: 'active' | 'closed' | 'completed' | 'expired';
  totalItems: number;
  countedItems: number;
  pendingItems: number;
  approvedItems: number;
  rejectedItems: number;
  pct: number;
  // F3: server-computed capability flags — guard all UI actions on these,
  // never on literal status strings (which have complex expiry/terminal interactions)
  canScan: boolean;
  canReview: boolean;
  canClose: boolean;
  canApprove: boolean;
  cycleId: number | null;
}

interface Props {
  onReview: (session: CountSession) => void;
  onSessionsLoaded?: (sessions: CountSession[]) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function locationLabel(loc: string): string {
  const MAP: Record<string, string> = {
    storeroom: 'Storeroom',
    working: 'Working Inventory',
  };
  return MAP[loc] ?? loc;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const OsCountSessionList: React.FC<Props> = ({ onReview, onSessionsLoaded }) => {
  const { apiBase } = useTenant();
  const [sessions, setSessions] = useState<CountSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/audit/sessions`);
      if (!res.ok) throw new Error(`Failed to load sessions (${res.status})`);
      const data = await res.json();
      const loaded: CountSession[] = data.sessions ?? [];
      setSessions(loaded);
      setError(null);
      onSessionsLoaded?.(loaded);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [apiBase, onSessionsLoaded]);

  // F5: real multi-select state — selectedIds is a Set of sessionId strings
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Per-session close results keyed by sessionId: 'ok' | 'error' | 'skipped'
  const [closeResults, setCloseResults] = useState<Record<string, 'ok' | 'error' | 'skipped'>>({});
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    fetchSessions();
    const id = setInterval(fetchSessions, 30_000);
    return () => clearInterval(id);
  }, [fetchSessions]);

  const toggleSelect = useCallback((sessionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId); else next.add(sessionId);
      return next;
    });
  }, []);

  // F5 fix: pass explicit IDs so this callback never reads stale `sessions` state.
  // The previous closeAllActive read sessions from closure but omitted it from deps,
  // always seeing the initial empty array and firing zero requests.
  const closeSelected = useCallback(async (idsToClose: string[]) => {
    if (!idsToClose.length) return;
    setClosing(true);
    const results: Record<string, 'ok' | 'error' | 'skipped'> = {};
    await Promise.all(
      idsToClose.map(async (sessionId) => {
        try {
          const res = await authFetch(
            `${apiBase}/audit/sessions/${sessionId}/close`,
            { method: 'POST' }
          );
          if (res.status === 409) {
            results[sessionId] = 'skipped'; // already closed — idempotent success
          } else if (!res.ok) {
            results[sessionId] = 'error';
          } else {
            results[sessionId] = 'ok';
          }
        } catch {
          results[sessionId] = 'error';
        }
      })
    );
    setCloseResults(results);
    setSelectedIds(new Set()); // clear selection; errors can be retried after refresh
    setClosing(false);
    await fetchSessions();
  }, [apiBase, fetchSessions]); // no `sessions` — IDs are passed explicitly

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress data-testid="session-list-loading" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="session-list-error">
        {error}
      </Alert>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="text.secondary" data-testid="session-list-empty">
          No count sessions yet.
        </Typography>
      </Box>
    );
  }

  const activeSessions = sessions.filter((s) => s.status === 'active');

  return (
    <Stack spacing={2} data-testid="session-list">
      {/* F5: selection controls + stop-counting action */}
      {selectedIds.size > 0 && (
        <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" flexWrap="wrap">
          {Object.entries(closeResults).filter(([, v]) => v === 'error').map(([id]) => (
            <Chip key={id} label={`${id.slice(0, 8)}… failed`} color="error" size="small" />
          ))}
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setSelectedIds(new Set())}
            data-testid="clear-selection-btn"
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={closing}
            onClick={() => closeSelected(Array.from(selectedIds))}
            data-testid="stop-counting-btn"
          >
            {closing ? 'Stopping…' : `Stop Counting (${selectedIds.size})`}
          </Button>
        </Box>
      )}
      {sessions.map((session) => (
        <Card
          key={session.sessionId}
          elevation={1}
          data-testid={`session-card-${session.sessionId}`}
          sx={session.status === 'closed' ? { opacity: 0.6 } : undefined}
        >
          <CardContent>
            {/* Header row: name + status chip + Review button */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              mb={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {/* F5: checkbox for closeable sessions */}
                {session.canClose && (
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(session.sessionId)}
                    onChange={() => toggleSelect(session.sessionId)}
                    inputProps={{ 'aria-label': `Select ${session.name}` }}
                    sx={{ mt: -0.5, ml: -1 }}
                    data-testid={`select-${session.sessionId}`}
                  />
                )}
                <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {session.name}
                  </Typography>
                  <Chip
                    label={session.status === 'active' ? 'ACTIVE' : session.status.toUpperCase()}
                    size="small"
                    color={
                      session.status === 'active' ? 'success'
                      : session.status === 'expired' ? 'warning'
                      : 'default'
                    }
                    data-testid={`status-chip-${session.sessionId}`}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Started {fmtDateTime(session.startedAt)} · {locationLabel(session.location)}
                </Typography>
              </Stack>
              </Stack>{/* close outer checkbox+name flex row */}

              <Button
                variant="contained"
                size="small"
                onClick={() => onReview(session)}
                // F3: use canReview/canClose capability flags, not literal status
                data-testid={`review-btn-${session.sessionId}`}
              >
                {session.status !== 'active' ? `View (${session.status})` : 'Review →'}
              </Button>
            </Stack>

            {/* Progress bar */}
            <Box mb={1}>
              <LinearProgress
                variant="determinate"
                value={session.pct}
                sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                aria-label={`${session.pct}% counted`}
                data-testid={`progress-bar-${session.sessionId}`}
              />
              <Typography variant="caption" color="text.secondary">
                {session.countedItems} / {session.totalItems} items counted
              </Typography>
            </Box>

            {/* Approval breakdown */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Typography variant="caption" color="warning.main">
                {session.pendingItems} pending approval
              </Typography>
              <Typography variant="caption" color="success.main">
                {session.approvedItems} approved
              </Typography>
              <Typography variant="caption" color="error.main">
                {session.rejectedItems} rejected
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};
